// Ghost notes — FL Studio's "ghost channels" over Live's arrangement: while
// one MIDI clip is open in the roll, the OTHER MIDI lanes' notes sounding
// at the same time are drawn dim behind it, so a bass line is written
// against the chords and a hat against the kick. Read-only, never proposed:
// a ghost is a picture of another clip, not an editable copy of it.
//
// A timeline clip's ghosts are the other lanes' timeline clips overlapping
// its span, their notes re-timed to this clip's own beats; a Session clip's
// ghosts are the other lanes' clips in the SAME scene (what launches with
// it), laid from its start and looped to fill it where they are shorter.
// A lane's own clips are never ghosted — they are what the roll edits.

import type { WireNote } from "@shared/midi/notes.js";
import type { Arrangement, Lane, LaneClip } from "@web-shared/lib/arrangement";

/** Notes of the other MIDI lanes under `clip`, in `clip`'s beats, with the
 * lane each came from so the roll can tint them apart. */
export interface GhostNote extends WireNote {
  laneId: string;
  laneName: string;
}

export function ghostNotesFor(arrangement: Arrangement, clip: LaneClip, laneId: string): GhostNote[] {
  const out: GhostNote[] = [];
  if (!(clip.duration > 0)) return out;
  for (const lane of arrangement.lanes) {
    if (lane.id === laneId || lane.kind !== "midi") continue;
    if (clip.slot !== undefined) {
      const other = lane.sessionClips.find((c) => c.slot === clip.slot && !c.ghost);
      if (other?.notes && other.duration > 0) {
        // The slot clip loops under this one: repeat it to fill the span.
        for (let offset = 0; offset < clip.duration - 1e-9; offset += other.duration) {
          for (const n of other.notes) pushTrimmed(out, n, offset, clip.duration, lane);
          if (!(other.duration > 0)) break;
        }
      }
      continue;
    }
    const from = clip.start;
    const to = clip.start + clip.duration;
    for (const other of lane.clips) {
      if (!other.notes || other.start >= to || other.start + other.duration <= from) continue;
      // Trim to the other clip's own length too: a note hanging past its
      // clip's end is silent there, as Live cuts it.
      const span = Math.min(other.duration, to - other.start);
      for (const n of other.notes) {
        if (n.startTime >= span - 1e-9) continue;
        const cut = { ...n, duration: Math.min(n.duration, span - n.startTime) };
        pushTrimmed(out, cut, other.start - from, clip.duration, lane);
      }
    }
  }
  return out;
}

/** `n` shifted by `offset` into a clip of `duration` beats, trimmed to it, or nothing. */
function pushTrimmed(out: GhostNote[], n: WireNote, offset: number, duration: number, lane: Lane): void {
  const start = n.startTime + offset;
  const end = Math.min(start + n.duration, duration);
  if (end <= 1e-9 || start >= duration - 1e-9) return;
  const startTime = Math.max(0, start);
  const length = end - startTime;
  if (length <= 1e-9) return;
  out.push({ pitch: n.pitch, startTime: r4(startTime), duration: r4(length), velocity: n.velocity, laneId: lane.id, laneName: lane.name });
}

function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}
